import React, { useMemo, useState } from "react";
import { Button, Upload, message, Radio } from "antd";
import { UploadOutlined } from "@ant-design/icons";
import API, { UPDATE_CONFIG, UPLOAD_FINAL_IMAGE } from "../../network/api";

const STORAGE_KEY = "final-image-upload-settings";
const MODE_GROUP = "GROUP_STAGE";
const MODE_FINAL = "FINAL";

function modeToIsKnockout(mode) {
  return mode === MODE_FINAL;
}

function readInitialSettings() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return {
        mode: MODE_GROUP,
        finalImageName: "",
        finalImageDataUrl: "",
      };
    }
    const parsed = JSON.parse(raw);
    const mode = parsed?.mode === MODE_FINAL ? MODE_FINAL : MODE_GROUP;
    const finalImageName =
      typeof parsed?.finalImageName === "string" ? parsed.finalImageName : "";
    const finalImageDataUrl =
      typeof parsed?.finalImageDataUrl === "string" ? parsed.finalImageDataUrl : "";
    return { mode, finalImageName, finalImageDataUrl };
  } catch (_) {
    return {
      mode: MODE_GROUP,
      finalImageName: "",
      finalImageDataUrl: "",
    };
  }
}

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

const FinalImageUploadTab = () => {
  const [messageApi, contextHolder] = message.useMessage();
  const init = useMemo(() => readInitialSettings(), []);
  const [mode, setMode] = useState(init.mode);
  const [finalImageName, setFinalImageName] = useState(init.finalImageName);
  const [finalImageDataUrl, setFinalImageDataUrl] = useState(init.finalImageDataUrl);
  const [finalImageFile, setFinalImageFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const beforeUpload = async (file) => {
    const isAllowedType = file.type === "image/jpeg" || file.type === "image/png";
    if (!isAllowedType) {
      messageApi.error("Chỉ hỗ trợ định dạng JPG/PNG.");
      return Upload.LIST_IGNORE;
    }
    const isLt5Mb = file.size / 1024 / 1024 < 5;
    if (!isLt5Mb) {
      messageApi.error("Kích thước ảnh tối đa 5MB.");
      return Upload.LIST_IGNORE;
    }
    try {
      const dataUrl = await fileToDataUrl(file);
      setFinalImageDataUrl(dataUrl);
      setFinalImageName(file.name || "uploaded-image");
      setFinalImageFile(file);
    } catch (_) {
      messageApi.error("Không đọc được file ảnh.");
    }
    return Upload.LIST_IGNORE;
  };

  const onSave = async () => {
    if (mode === MODE_FINAL && !finalImageDataUrl) {
      messageApi.error("Vui lòng upload ảnh khi chọn Trận chung kết.");
      return;
    }
    setSaving(true);
    try {
      await API.patch(UPDATE_CONFIG, {
        config: {
          isKnockout: modeToIsKnockout(mode),
        },
      });

      if (mode === MODE_FINAL) {
        if (!finalImageFile) {
          messageApi.error("Vui lòng chọn lại file ảnh trước khi lưu.");
          return;
        }
        const body = new FormData();
        body.append("file", finalImageFile);
        await API.post(UPLOAD_FINAL_IMAGE, body, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      }
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({
          mode,
          finalImageName,
          finalImageDataUrl,
        })
      );
      messageApi.success(
        mode === MODE_FINAL
          ? "Đã upload ảnh chung kết thành công."
          : "Đã lưu cài đặt hiển thị."
      );
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        err?.message ||
        "Upload ảnh thất bại.";
      messageApi.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="col-12 px-1">
      {contextHolder}

      <div style={{ border: "1px solid #f0f0f0", borderRadius: 6, padding: 12 }}>
        <div style={{ fontWeight: 600, marginBottom: 12 }}>Chế độ hiển thị</div>

        <Radio.Group
          onChange={(e) => setMode(e.target.value)}
          value={mode}
          style={{ display: "flex", flexDirection: "column", gap: 8 }}
        >
          <Radio value={MODE_GROUP}>Trận đấu vòng bảng</Radio>
          <Radio value={MODE_FINAL}>Trận chung kết</Radio>
        </Radio.Group>

        {mode === MODE_FINAL && (
          <div style={{ marginTop: 20 }}>
            <div style={{ fontWeight: 600, marginBottom: 10 }}>Chọn ảnh</div>

            <Upload
              accept=".jpg,.jpeg,.png,image/jpeg,image/png"
              beforeUpload={beforeUpload}
              showUploadList={false}
              maxCount={1}
            >
              <Button icon={<UploadOutlined />}>Upload ảnh</Button>
            </Upload>

            <div style={{ marginTop: 10, color: "#6b7280", fontSize: 12 }}>
              Kích thước đề xuất: 1080 x 1920 px
              <br />
              Định dạng: JPG, PNG
            </div>

            {finalImageDataUrl ? (
              <div style={{ marginTop: 14 }}>
                <img
                  src={finalImageDataUrl}
                  alt={finalImageName || "final-preview"}
                  style={{
                    width: 170,
                    height: 220,
                    objectFit: "cover",
                    border: "1px solid #f0f0f0",
                    borderRadius: 6,
                  }}
                />
                <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280" }}>
                  {finalImageName}
                </div>
              </div>
            ) : null}
          </div>
        )}

        <div style={{ marginTop: 20 }}>
          <Button type="primary" loading={saving} onClick={onSave}>
            Lưu
          </Button>
        </div>
      </div>
    </div>
  );
};

export default FinalImageUploadTab;

